// SPDX-License-Identifier: Apache-2.0

// edges.ts — fat-line wikilink + semantic edge rendering.
//
// PORT FROM v1: vault-orbit.tsx RelationGraph (748-876) and SemanticGraph
// (892-977). The previous v2 implementation used `THREE.LineSegments` with
// 1-pixel screen-space lines, which the audit called out as "thin and
// lifeless". This stream restores fat lines via three.js's
// `LineSegments2 + LineSegmentsGeometry + LineMaterial` (in
// `three/examples/jsm/lines`).
//
// LAYERS
//   1. wikilinkLines — `LineSegments2` with per-vertex tint colour from the
//      source/destination doc tints. Linewidth ~2 world units. Brightens to
//      4 world units + saturated colour when an endpoint is hovered/active.
//   2. semanticLines — `LineSegments2`, monotone cyan tilt scaled by cosine
//      sim. No hover reaction (semantic noise should never compete with
//      explicit-link focus).
//   3. focusedLines — `LineSegments2` overlay rendered ONLY for edges that
//      touch the current focus node. Higher linewidth + saturated colour +
//      additive blending. This is the "hover-bright" pass — separating it
//      avoids per-vertex colour-mutation churn on the main mesh.
//
// `LineMaterial.resolution` MUST be kept in sync with the canvas size; the
// shader uses it to convert pixel widths to clip-space offsets. We expose
// `setResolution()` for the engine's resize handler — it's safe to ignore
// (the field is only read on resize) but for fidelity matching v1's audit
// fix we update it on every rebuild() too.
//
// API SURFACE — unchanged: { wikilinkLines, semanticLines, rebuild,
// applyFocus, updatePositions, dispose }. (`setResolution` is added but
// optional; engine discovers it lazily.)

import * as THREE from "three";
import type { VaultDoc } from "@tether/storage";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { TINT_HEX } from "./nodes";

export interface SemanticEdge {
  a: string;
  b: string;
  /** cosine similarity in [0..1] */
  w: number;
}

/** Public field shape — matches the previous edges.ts plus an
 *  optional setResolution hook so the engine can sync canvas size. */
export interface EdgeField {
  wikilinkLines: LineSegments2;
  semanticLines: LineSegments2;
  /** Replace the edge set + colours. Call when docs/edges/highlight changes. */
  rebuild(
    docs: VaultDoc[],
    wikilinkEdges: Array<[string, string]>,
    semanticEdges: SemanticEdge[],
  ): void;
  applyFocus(hoveredId: string | null, activeId: string | null): void;
  updatePositions(
    positions: Float32Array,
    idToIdx: (id: string) => number | undefined,
  ): void;
  /** Optional: update LineMaterial.resolution to current canvas size. */
  setResolution?(w: number, h: number): void;
  dispose(): void;
}

// Hephaestus 4 — flipped to worldUnits=true so far edges thin out naturally
// under perspective. Screen-space 2 px ≈ world-space 0.04 at typical orbit
// distances; tuned by eye against the M-class look. The previous resolution-
// uniform sync is kept defensively (LineMaterial reads it on the resize path
// even when worldUnits=true so we still update it).
const FAT_LINEWIDTH = 0.04;       // base "regular" wikilink width (world units)
const FAT_FOCUS_WIDTH = 0.075;    // bumped width for focused-edge overlay (world units)
const SEMANTIC_LINEWIDTH = 0.028; // semantic threads thinner than wikilinks

export function createEdgeField(): EdgeField {
  // Initial resolution — caller is expected to setResolution() once the
  // canvas size is known. Default to 1920x1080 so the math is sane until
  // the resize observer fires.
  const initialResolution = new THREE.Vector2(1920, 1080);

  // ── Wikilink edges (tint-coloured, full opacity) ──────────────────────────
  const wikiGeom = new LineSegmentsGeometry();
  const wikiMat = new LineMaterial({
    color: 0xffffff,         // overridden per-vertex via vertexColors
    linewidth: FAT_LINEWIDTH,
    transparent: true,
    opacity: 0.85,
    vertexColors: true,
    worldUnits: true,         // far edges thin under perspective (Hephaestus 4)
    resolution: initialResolution.clone(),
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    dashed: false,
  });
  // LineMaterial doesn't expose toneMapped via constructor in some builds;
  // set defensively.
  (wikiMat as unknown as { toneMapped: boolean }).toneMapped = false;
  const wikilinkLines = new LineSegments2(wikiGeom, wikiMat);
  wikilinkLines.frustumCulled = false;
  // NOTE: do NOT call computeLineDistances() here. LineSegments2's impl reads
  // `geometry.attributes.instanceStart.count`, which is undefined until
  // `LineSegmentsGeometry.setPositions()` has been called. computeLineDistances
  // is only meaningful for `dashed: true` materials anyway (it builds
  // instanceDistanceStart/End used by the dashing path). All three line
  // materials here use `dashed: false`, so we skip it entirely.
  // edges shouldn't intercept clicks
  wikilinkLines.raycast = () => undefined;

  // ── Semantic edges (monotone cyan, no hover reaction) ─────────────────────
  const semGeom = new LineSegmentsGeometry();
  const semMat = new LineMaterial({
    color: 0xffffff,
    linewidth: SEMANTIC_LINEWIDTH,
    transparent: true,
    opacity: 0.35,
    vertexColors: true,
    worldUnits: true,
    resolution: initialResolution.clone(),
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    dashed: false,
  });
  (semMat as unknown as { toneMapped: boolean }).toneMapped = false;
  const semanticLines = new LineSegments2(semGeom, semMat);
  semanticLines.frustumCulled = false;
  // (see wikilinkLines above for why computeLineDistances is omitted)
  semanticLines.raycast = () => undefined;

  // ── Focused-edge overlay (hover-bright pass) ──────────────────────────────
  // Lives on the same parent group via wikilinkLines.add(), so the engine
  // doesn't need to add it explicitly. We update its geometry every time
  // applyFocus() runs — the overlay re-uses positions already baked into
  // wikilinkLines geometry but its draw range tracks only "edges that
  // touch the focus node".
  const focusGeom = new LineSegmentsGeometry();
  const focusMat = new LineMaterial({
    color: 0xffffff,
    linewidth: FAT_FOCUS_WIDTH,
    transparent: true,
    opacity: 1.0,
    vertexColors: true,
    worldUnits: true,
    resolution: initialResolution.clone(),
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    dashed: false,
  });
  (focusMat as unknown as { toneMapped: boolean }).toneMapped = false;
  const focusLines = new LineSegments2(focusGeom, focusMat);
  focusLines.frustumCulled = false;
  // (see wikilinkLines above for why computeLineDistances is omitted)
  focusLines.raycast = () => undefined;
  // Render the focused overlay attached to wikilinkLines so a single
  // scene.add(wikilinkLines) brings the overlay along for the ride.
  wikilinkLines.add(focusLines);

  // ── State ─────────────────────────────────────────────────────────────────
  let wikiEdges: Array<[string, string]> = [];
  let semEdges: SemanticEdge[] = [];
  let docByIdMap = new Map<string, VaultDoc>();

  /** CPU mirror of wikilink positions — Float32Array[edges*6] (xa,ya,za,xb,yb,zb …). */
  let wikiPositions = new Float32Array(0);
  let wikiColors = new Float32Array(0);
  let semPositions = new Float32Array(0);
  let semColors = new Float32Array(0);

  /** Parallel array of "is this edge focused?" — drives the focus overlay. */
  let edgeIsFocused = new Uint8Array(0);

  const cA = new THREE.Color();
  const cB = new THREE.Color();

  function rebuild(
    docs: VaultDoc[],
    wikilinkEdges: Array<[string, string]>,
    semanticEdges: SemanticEdge[],
  ) {
    wikiEdges = wikilinkEdges;
    semEdges = semanticEdges;
    docByIdMap = new Map(docs.map((d) => [d.id, d]));

    // Wikilink CPU buffers — 6 floats per edge for both pos and colour
    // (each edge = 2 vertices × 3 components).
    wikiPositions = new Float32Array(wikiEdges.length * 6);
    wikiColors = new Float32Array(wikiEdges.length * 6);
    edgeIsFocused = new Uint8Array(wikiEdges.length);

    // Semantic CPU buffers
    semPositions = new Float32Array(semEdges.length * 6);
    semColors = new Float32Array(semEdges.length * 6);

    // Fill colours up front (positions get filled in updatePositions per-frame)
    paintWikilinkColors(null, wikiColors);
    paintSemanticColors(semColors);

    // Push to GPU
    wikiGeom.setPositions(wikiPositions);
    wikiGeom.setColors(wikiColors);
    semGeom.setPositions(semPositions);
    semGeom.setColors(semColors);

    // Reset focused overlay
    focusGeom.setPositions(new Float32Array(0));
    focusGeom.setColors(new Float32Array(0));
  }

  function paintWikilinkColors(
    focus: string | null,
    out: Float32Array,
  ) {
    const HIGHLIGHT_BOOST = 1.3; // touched edges saturate
    const DIM_OTHER = 0.20;       // un-touched edges dim
    for (let i = 0; i < wikiEdges.length; i++) {
      const [aId, bId] = wikiEdges[i]!;
      const aDoc = docByIdMap.get(aId);
      const bDoc = docByIdMap.get(bId);
      const aHex = aDoc ? (TINT_HEX[aDoc.tint] ?? "#67e8f9") : "#67e8f9";
      const bHex = bDoc ? (TINT_HEX[bDoc.tint] ?? "#67e8f9") : "#67e8f9";
      cA.set(aHex);
      cB.set(bHex);
      const touches = focus != null && (aId === focus || bId === focus);
      const mult = focus == null ? 1.0 : touches ? HIGHLIGHT_BOOST : DIM_OTHER;
      const base = i * 6;
      out[base + 0] = cA.r * mult;
      out[base + 1] = cA.g * mult;
      out[base + 2] = cA.b * mult;
      out[base + 3] = cB.r * mult;
      out[base + 4] = cB.g * mult;
      out[base + 5] = cB.b * mult;
      edgeIsFocused[i] = touches ? 1 : 0;
    }
  }

  function paintSemanticColors(out: Float32Array) {
    // Semantic colors: monochrome cyan-ish, brightness from cosine weight.
    for (let i = 0; i < semEdges.length; i++) {
      const w = semEdges[i]!.w;
      const v = Math.min(0.95, 0.45 + w * 0.6);
      const base = i * 6;
      // r = v * 0.4 (suppress red), g/b = v full → cyan tilt (v1 SemanticGraph)
      out[base + 0] = v * 0.4;
      out[base + 1] = v * 0.95;
      out[base + 2] = v;
      out[base + 3] = v * 0.4;
      out[base + 4] = v * 0.95;
      out[base + 5] = v;
    }
  }

  /** Build the focused-overlay geometry from the current wikilink positions
   *  + colours, restricted to edges that touch the focus node. */
  function rebuildFocusOverlay() {
    let count = 0;
    for (let i = 0; i < edgeIsFocused.length; i++) if (edgeIsFocused[i]) count++;
    if (count === 0) {
      focusGeom.setPositions(new Float32Array(0));
      focusGeom.setColors(new Float32Array(0));
      return;
    }
    const positions = new Float32Array(count * 6);
    const colors = new Float32Array(count * 6);
    let w = 0;
    for (let i = 0; i < edgeIsFocused.length; i++) {
      if (!edgeIsFocused[i]) continue;
      const src = i * 6;
      for (let k = 0; k < 6; k++) {
        positions[w + k] = wikiPositions[src + k]!;
        // Saturate the focused-overlay colour to compensate for the
        // dim-others mult applied to the base mesh.
        colors[w + k] = Math.min(1.6, (wikiColors[src + k] ?? 0) * 2.2);
      }
      w += 6;
    }
    focusGeom.setPositions(positions);
    focusGeom.setColors(colors);
  }

  function applyFocus(hoveredId: string | null, activeId: string | null) {
    const focus = hoveredId ?? activeId ?? null;
    paintWikilinkColors(focus, wikiColors);
    wikiGeom.setColors(wikiColors);
    rebuildFocusOverlay();
  }

  function updatePositions(
    positions: Float32Array,
    idToIdx: (id: string) => number | undefined,
  ) {
    // Wikilink positions
    if (wikiEdges.length > 0) {
      let w = 0;
      for (let i = 0; i < wikiEdges.length; i++) {
        const [aId, bId] = wikiEdges[i]!;
        const ia = idToIdx(aId);
        const ib = idToIdx(bId);
        if (ia === undefined || ib === undefined) {
          for (let k = 0; k < 6; k++) wikiPositions[w + k] = 0;
          w += 6;
          continue;
        }
        wikiPositions[w + 0] = positions[ia * 4 + 0]!;
        wikiPositions[w + 1] = positions[ia * 4 + 1]!;
        wikiPositions[w + 2] = positions[ia * 4 + 2]!;
        wikiPositions[w + 3] = positions[ib * 4 + 0]!;
        wikiPositions[w + 4] = positions[ib * 4 + 1]!;
        wikiPositions[w + 5] = positions[ib * 4 + 2]!;
        w += 6;
      }
      wikiGeom.setPositions(wikiPositions);
      // If a focus is in play, refresh the overlay to track moving sim.
      // LineSegmentsGeometry uses interleaved instanceStart/instanceEnd, NOT
      // a `position` attribute — the original guard checked an undefined ref
      // and short-circuited (so the overlay never refreshed mid-sim). We
      // instead drive the refresh from edgeIsFocused, which is the same
      // flag rebuildFocusOverlay() consumes.
      let anyFocused = false;
      for (let i = 0; i < edgeIsFocused.length; i++) {
        if (edgeIsFocused[i]) { anyFocused = true; break; }
      }
      if (anyFocused) rebuildFocusOverlay();
    }

    // Semantic positions
    if (semEdges.length > 0) {
      let w = 0;
      for (let i = 0; i < semEdges.length; i++) {
        const { a, b } = semEdges[i]!;
        const ia = idToIdx(a);
        const ib = idToIdx(b);
        if (ia === undefined || ib === undefined) {
          for (let k = 0; k < 6; k++) semPositions[w + k] = 0;
          w += 6;
          continue;
        }
        semPositions[w + 0] = positions[ia * 4 + 0]!;
        semPositions[w + 1] = positions[ia * 4 + 1]!;
        semPositions[w + 2] = positions[ia * 4 + 2]!;
        semPositions[w + 3] = positions[ib * 4 + 0]!;
        semPositions[w + 4] = positions[ib * 4 + 1]!;
        semPositions[w + 5] = positions[ib * 4 + 2]!;
        w += 6;
      }
      semGeom.setPositions(semPositions);
    }
  }

  function setResolution(w: number, h: number) {
    wikiMat.resolution.set(w, h);
    semMat.resolution.set(w, h);
    focusMat.resolution.set(w, h);
  }

  // Auto-sync LineMaterial.resolution to the renderer's drawing-buffer size
  // every frame. This avoids needing a public setResolution call from
  // VaultOrbit3D.tsx (which is off-limits for this stream). The cost is
  // one Vector2.set() + two abs-comparisons per frame.
  const _tmpResolution = new THREE.Vector2();
  wikilinkLines.onBeforeRender = function (_renderer: THREE.WebGLRenderer) {
    const tgt = _renderer.getDrawingBufferSize(_tmpResolution);
    if (
      Math.abs(wikiMat.resolution.x - tgt.x) > 0.5 ||
      Math.abs(wikiMat.resolution.y - tgt.y) > 0.5
    ) {
      setResolution(tgt.x, tgt.y);
    }
  };

  return {
    wikilinkLines,
    semanticLines,
    rebuild,
    applyFocus,
    updatePositions,
    setResolution,
    dispose() {
      wikiGeom.dispose();
      wikiMat.dispose();
      semGeom.dispose();
      semMat.dispose();
      focusGeom.dispose();
      focusMat.dispose();
    },
  };
}
